class CustomHeaderMenu extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-header');
    this.abortController = new AbortController();
    this.hydrate();
    this.items = [...this.querySelectorAll('[data-custom-menu-item]')];
    const options = { signal: this.abortController.signal };
    this.initializeMobileCarousels(options);
    this.initializeMobileAccordions(options);
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.setHeaderPinned(true);
    }, options);
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.setHeaderPinned(false);
    }, options);
    this.addEventListener('focusin', () => this.setHeaderPinned(true), options);
    this.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.contains(document.activeElement)) this.setHeaderPinned(this.matches(':hover'));
    }), options);
    this.items.forEach((item) => {
      const button = item.querySelector(':scope > .custom-menu__trigger .custom-menu__toggle');
      if (!button) return;
      button.addEventListener('click', () => this.toggle(item), options);
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') {
          clearTimeout(this.closeTimer);
          this.open(item);
        }
      }, options);
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && !item.contains(document.activeElement)) {
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => {
            if (!item.matches(':hover') && !item.contains(document.activeElement)) this.close(item);
          }, 150);
        }
      }, options);
      item.addEventListener('focusin', () => this.open(item), options);
      item.addEventListener('focusout', () => queueMicrotask(() => {
        if (!item.contains(document.activeElement)) this.close(item);
      }), options);
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeAll();
    }, options);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const openItem = this.items.find((item) => item.querySelector('.custom-menu__toggle[aria-expanded="true"]'));
        if (openItem) {
          const button = openItem.querySelector('.custom-menu__toggle');
          this.close(openItem);
          button.focus();
        }
      }
    }, options);
    window.addEventListener('scroll', () => {
      if (this.isHeaderPinned()) return;
      this.pendingOpenItem = null;
      this.closeAll();
      clearTimeout(this.restoreHoverTimer);
      this.restoreHoverTimer = setTimeout(() => this.restoreHoveredItem(), 160);
    }, { ...options, passive: true });
    this.section.addEventListener('transitionend', (event) => {
      if (event.target === this.section && event.propertyName === 'top') {
        this.restoreHoveredItem();
      }
    }, options);
    window.addEventListener('resize', () => this.scheduleMeasure(), options);
    document.addEventListener('shopify:block:select', (event) => {
      const source = event.target.matches('[data-custom-menu-source]')
        ? event.target
        : event.target.querySelector('[data-custom-menu-source]');
      const blockId = source?.dataset.blockId;
      const item = blockId && this.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
      if (item) this.open(item);
      const mobileSource = event.target.matches('[data-custom-menu-mobile-source]')
        ? event.target
        : event.target.closest('[data-custom-menu-mobile-source]') || event.target.querySelector('[data-custom-menu-mobile-source]');
      if (mobileSource) this.openMobileDrawer();
      this.scheduleMeasure();
    }, options);
    document.addEventListener('shopify:block:deselect', () => this.scheduleMeasure(), options);
    const sourceContainer = this.section.querySelector('.header__theme-blocks');
    if (sourceContainer && window.Shopify?.designMode) {
      this.sourceObserver = new MutationObserver(() => this.scheduleRebuild());
      this.sourceObserver.observe(sourceContainer, { childList: true, subtree: true, attributes: true, characterData: true });
    }
    this.measure();
  }

  scheduleRebuild() {
    cancelAnimationFrame(this.rebuildFrame);
    this.rebuildFrame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.querySelector('[data-custom-menu-desktop-list]').replaceChildren();
      this.section.querySelector('[data-custom-menu-drawer-list]')?.replaceChildren();
      this.section.querySelector('[data-custom-menu-mobile-carousel-slot]')?.replaceChildren();
      this.section.querySelector('[data-custom-menu-mobile-contact-slot]')?.replaceChildren();
      this.section.querySelector('[data-custom-menu-mobile-cta-slot]')?.replaceChildren();
      this.replaceWith(this.cloneNode(true));
    });
  }

  hydrate() {
    const desktopList = this.querySelector('[data-custom-menu-desktop-list]');
    const drawerList = this.section.querySelector('[data-custom-menu-drawer-list]');
    const ctaSlot = this.section.querySelector('[data-custom-menu-mobile-cta-slot]');
    this.section.querySelectorAll('[data-custom-menu-source]').forEach((source) => {
      const desktopTemplate = source.querySelector('[data-custom-menu-desktop-template]');
      const drawerTemplate = source.querySelector('[data-custom-menu-drawer-template]');
      const ctaTemplate = source.querySelector('[data-custom-menu-drawer-cta-template]');
      if (desktopTemplate) desktopList.append(desktopTemplate.content.cloneNode(true));
      if (drawerTemplate && drawerList) drawerList.append(drawerTemplate.content.cloneNode(true));
      if (ctaTemplate && ctaSlot) ctaSlot.append(ctaTemplate.content.cloneNode(true));
    });

    this.applyLinkThumbnails();

    const mobileSource = this.section.querySelector('[data-custom-menu-mobile-source]');
    const carouselTemplate = mobileSource?.querySelector('[data-custom-menu-mobile-carousel-template]');
    const accountLabelTemplate = mobileSource?.querySelector('[data-custom-menu-mobile-account-label-template]');
    const contactTemplate = mobileSource?.querySelector('[data-custom-menu-mobile-contact-template]');
    const carouselSlot = this.section.querySelector('[data-custom-menu-mobile-carousel-slot]');
    const contactSlot = this.section.querySelector('[data-custom-menu-mobile-contact-slot]');
    if (carouselTemplate && carouselSlot) carouselSlot.append(carouselTemplate.content.cloneNode(true));
    const accountLabel = this.section.querySelector('[data-custom-menu-mobile-account-label]');
    if (accountLabelTemplate && accountLabel) accountLabel.replaceChildren(accountLabelTemplate.content.cloneNode(true));
    if (contactTemplate && contactSlot) contactSlot.append(contactTemplate.content.cloneNode(true));

    const drawer = this.section.querySelector('header-drawer');
    if (!drawer || !drawerList) return;
    drawerList.querySelectorAll(':scope > li > details > summary').forEach((summary) => {
      summary.addEventListener('click', () => {
        if (summary.parentElement.open) return;
        summary.parentElement.querySelectorAll('.custom-menu-drawer__accordion').forEach((accordion, index) => {
          accordion.open = index === 0;
        });
      });
      summary.addEventListener('click', drawer.onSummaryClick.bind(drawer));
    });
    drawerList.querySelectorAll('.menu-drawer__close-button').forEach((button) => {
      button.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
    });
  }

  normalizeMenuUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const pathname = url.pathname.length > 1 ? url.pathname.replace(/\/$/, '') : url.pathname;
      return `${url.origin}${pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return value;
    }
  }

  applyLinkThumbnails() {
    const roots = [
      ...this.querySelectorAll('[data-custom-menu-item]'),
      ...this.section.querySelectorAll('[data-custom-drawer-item]'),
    ];

    roots.forEach((root) => {
      root.querySelectorAll('[data-custom-menu-thumbnail]').forEach((config) => {
        const targetUrl = this.normalizeMenuUrl(config.dataset.targetUrl);
        if (!targetUrl) return;

        root.querySelectorAll('[data-custom-menu-link]').forEach((link) => {
          if (this.normalizeMenuUrl(link.getAttribute('href')) !== targetUrl) return;
          if (link.dataset.customMenuThumbnailApplied === 'true') return;

          const thumbnail = config.content.firstElementChild?.cloneNode(true);
          if (thumbnail) {
            link.prepend(thumbnail);
            link.classList.add('custom-menu__link-with-thumbnail');
          }
          if (config.dataset.linkBold === 'true') link.classList.add('custom-menu__link--bold');
          if (config.dataset.linkUnderlined === 'true') link.classList.add('custom-menu__link--underlined');
          link.dataset.customMenuThumbnailApplied = 'true';

          if (config.dataset.linkEnabled === 'false') {
            const label = document.createElement('span');
            const staticClasses = [...link.classList].filter(
              (className) => !['focus-inset', 'link', 'link--text'].includes(className)
            );
            label.className = [...staticClasses, 'custom-menu__link-with-thumbnail--static'].join(' ');
            label.innerHTML = link.innerHTML;
            link.replaceWith(label);
          }
        });
      });
    });
  }

  initializeMobileCarousels(options) {
    this.section.querySelectorAll('[data-custom-menu-mobile-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-custom-menu-mobile-track]');
      const progress = carousel.querySelector('[data-custom-menu-mobile-progress]');
      const slides = track?.querySelectorAll('[data-custom-menu-mobile-slide]') || [];
      if (!track || !progress || !slides.length) return;

      const update = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const ratio = maxScroll ? Math.min(1, Math.abs(track.scrollLeft) / maxScroll) : 0;
        progress.parentElement.hidden = false;
        progress.style.width = `${ratio * 100}%`;
      };

      track.addEventListener('scroll', () => requestAnimationFrame(update), { ...options, passive: true });
      window.addEventListener('resize', update, options);
      update();
    });
  }

  initializeMobileAccordions(options) {
    this.section.querySelectorAll('.custom-menu-drawer__accordion').forEach((accordion) => {
      let scrollFrame;
      const updateExpandedState = (scrollToAccordion = false) => {
        accordion.querySelector(':scope > summary')?.setAttribute('aria-expanded', accordion.open);
        if (!accordion.open) return;

        const container = accordion.parentElement;
        container.querySelectorAll(':scope > .custom-menu-drawer__accordion').forEach((sibling) => {
          if (sibling !== accordion) sibling.open = false;
        });

        if (!scrollToAccordion) return;
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          if (!accordion.open) return;
          const containerRect = container.getBoundingClientRect();
          const accordionRect = accordion.getBoundingClientRect();
          const scrollOffset = Number.parseFloat(window.getComputedStyle(container).paddingTop) || 0;
          container.scrollTo({
            top: container.scrollTop + accordionRect.top - containerRect.top - scrollOffset,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        });
      };
      updateExpandedState();
      accordion.addEventListener('toggle', () => updateExpandedState(true), options);
    });
  }

  openMobileDrawer() {
    if (!window.matchMedia('(max-width: 989px)').matches) return;
    const drawer = this.section.querySelector('header-drawer');
    const details = drawer?.mainDetailsToggle;
    if (details && !details.open) details.querySelector(':scope > summary')?.click();
  }

  toggle(item) {
    const expanded = item.querySelector('.custom-menu__toggle').getAttribute('aria-expanded') === 'true';
    expanded ? this.close(item) : this.open(item);
  }

  open(item) {
    const panel = item.querySelector('[data-custom-menu-panel]');
    if (!panel) return;
    const headerTransitions = this.section.getAnimations?.().filter((animation) =>
      animation.playState === 'running' && animation.transitionProperty === 'top'
    ) || [];
    if (headerTransitions.length) {
      this.pendingOpenItem = item;
      Promise.allSettled(headerTransitions.map((animation) => animation.finished)).then(() => {
        if (this.pendingOpenItem !== item || !this.isConnected) return;
        this.pendingOpenItem = null;
        if (item.matches(':hover') || item.contains(document.activeElement)) this.open(item);
      });
      return;
    }
    this.pendingOpenItem = null;
    this.measure();
    const header = this.closest('.header-wrapper');
    if (header) document.documentElement.style.setProperty('--header-bottom-position', `${Math.round(header.getBoundingClientRect().bottom)}px`);
    this.closeAll(item);
    item.querySelector('.custom-menu__toggle').setAttribute('aria-expanded', 'true');
    clearTimeout(panel.hideTimer);
    panel.hidden = false;
    panel.getBoundingClientRect();
    panel.classList.add('is-open');
    this.notifyInteractionChange();
  }

  close(item) {
    const button = item.querySelector('.custom-menu__toggle');
    const panel = item.querySelector('[data-custom-menu-panel]');
    if (button) button.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.classList.remove('is-open');
      clearTimeout(panel.hideTimer);
      const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 150;
      panel.hideTimer = setTimeout(() => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, duration);
    }
    this.notifyInteractionChange();
  }

  notifyInteractionChange() {
    this.dispatchEvent(new CustomEvent('header:interaction-change', { bubbles: true }));
  }

  closeAll(exception) { this.items.forEach((item) => { if (item !== exception) this.close(item); }); }

  setHeaderPinned(pinned) {
    const stickyHeader = this.closest('sticky-header');
    if (stickyHeader?.setCustomMenuPinned) {
      stickyHeader.setCustomMenuPinned(pinned);
    } else if (stickyHeader) {
      stickyHeader.customMenuPreventHide = pinned;
    }
  }

  isHeaderPinned() {
    return this.matches(':hover') || this.contains(document.activeElement);
  }

  restoreHoveredItem() {
    if (this.section.classList.contains('shopify-section-header-hidden')) return;
    const hoveredItem = this.items.find((item) => item.matches(':hover'));
    if (hoveredItem) this.open(hoveredItem);
  }

  scheduleMeasure() {
    cancelAnimationFrame(this.measureFrame);
    this.measureFrame = requestAnimationFrame(() => this.measure());
  }

  measure() {
    const middleLeftHeader = this.section.querySelector('.header--middle-left');
    if (middleLeftHeader) {
      const headerBounds = middleLeftHeader.getBoundingClientRect();
      const headerStyle = getComputedStyle(middleLeftHeader);
      const cartIcon = middleLeftHeader.querySelector('#cart-icon-bubble');
      const contentLeft = headerBounds.left + parseFloat(headerStyle.paddingLeft);
      const cartBounds = cartIcon?.getBoundingClientRect();
      const contentRight = cartBounds
        ? window.innerWidth - cartBounds.right
        : window.innerWidth - headerBounds.right + parseFloat(headerStyle.paddingRight);
      this.style.setProperty('--custom-menu-content-left', `${Math.round(contentLeft)}px`);
      this.style.setProperty('--custom-menu-content-right', `${Math.round(contentRight)}px`);
    }
    const header = this.closest('.header-wrapper');
    if (header) document.documentElement.style.setProperty('--header-bottom-position', `${Math.round(header.getBoundingClientRect().bottom)}px`);
  }

  disconnectedCallback() {
    this.setHeaderPinned(false);
    this.abortController.abort();
    clearTimeout(this.closeTimer);
    clearTimeout(this.restoreHoverTimer);
    this.sourceObserver?.disconnect();
    cancelAnimationFrame(this.measureFrame);
    cancelAnimationFrame(this.rebuildFrame);
  }
}

if (!customElements.get('custom-header-menu')) customElements.define('custom-header-menu', CustomHeaderMenu);
